import { useTranslation } from 'react-i18next'

import BasqueLogo from '@/assets/images/basque-logo.svg?react'
import GOELogo from '@/assets/images/tipi-goe.svg?react'
import { ChromaKeyVideo } from '@/components/food-cost/ChromaKeyVideo'
import { ASSETS, CHROMA_KEY_SETTINGS } from '@/components/food-cost/constants'
import { FoodCostHeroCollaboration } from '@/components/food-cost/FoodCostHeroCollaboration'
import { TIPI_BUSINESS_COUNT } from '@/lib/landingStats/constants'

import type { SeoKeywordLandingId } from './catalog'
import { seoKeywordI18nPrefix } from './catalog'

const HERO_CTA_CLASS =
    'flex min-h-[clamp(2.375rem,6vh,3.5rem)] w-full max-w-[420px] items-center justify-center rounded-full bg-[#F4502A] px-[clamp(0.625rem,3.2vw,1.5rem)] py-[clamp(0.4375rem,1vh,0.875rem)] text-center text-[length:clamp(0.6875rem,min(3.1vw,1.9vh),0.9375rem)] font-bold leading-[1.15] text-white transition-colors hover:bg-[#E04A28] [@media(max-height:740px)]:min-h-[2.375rem] [@media(max-height:740px)]:px-3 [@media(max-height:740px)]:py-2'

interface SeoKeywordHeroProps {
    landingId: SeoKeywordLandingId
    onCtaClick: () => void
}

export function SeoKeywordHero({ landingId, onCtaClick }: SeoKeywordHeroProps) {
    const { t } = useTranslation()
    const prefix = seoKeywordI18nPrefix(landingId)
    const primaryCta = t(`${prefix}.hero.cta`)

    return (
        <section
            data-foodcost-hero
            className='relative flex h-dvh max-h-[100dvh] flex-col overflow-hidden bg-[#17130E] bg-[radial-gradient(ellipse_120%_55%_at_50%_62%,rgba(244,80,42,0.22)_0%,rgba(244,80,42,0.05)_45%,rgba(23,19,14,0)_70%)] pt-[clamp(1rem,3vh,2.5rem)]'
        >
            <div className='mx-auto grid h-full min-h-0 w-full max-w-[1180px] flex-1 grid-cols-1 content-center gap-[clamp(0.75rem,2.2vh,2.5rem)] px-[22px] pb-[clamp(0.75rem,2vh,2rem)] md:grid-cols-[minmax(0,1.05fr)_minmax(260px,0.95fr)] md:items-center md:gap-8 md:px-10 md:pb-[clamp(1rem,2.5vh,3rem)]'>
                <div className='relative flex min-h-0 flex-col gap-[clamp(0.65rem,1.8vh,1.125rem)] md:max-w-[560px] md:self-center'>
                    <div className='inline-flex w-fit items-center gap-2 rounded-full border border-[#F5EEE4]/15 bg-[#F5EEE4]/5 px-[15px] py-[clamp(0.35rem,1vh,0.5rem)] text-[clamp(0.75rem,1.8vw,0.8125rem)] font-medium text-[#E8DFD2]'>
                        <span className='inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-[#1FA855]' aria-hidden />
                        <span>
                            {t(`${prefix}.hero.clientBadge`, {
                                count: TIPI_BUSINESS_COUNT,
                            })}
                        </span>
                    </div>

                    <h1 className='m-0 max-w-[18ch] text-balance text-[length:clamp(1.875rem,min(11.5vw,7.25vh),3.25rem)] font-medium leading-[1.02] tracking-[-0.03em] text-[#F5EEE4] md:max-w-[20ch]'>
                        {t(`${prefix}.hero.title`)}
                    </h1>

                    <p className='m-0 max-w-[34ch] text-[length:clamp(0.9375rem,2.4vw,1.25rem)] font-normal leading-[1.5] text-[#B8AFA3] md:max-w-[40ch]'>
                        {t(`${prefix}.hero.subtitle`)}
                    </p>

                    <div className='hidden flex-col gap-1.5 md:flex'>
                        <button type='button' onClick={onCtaClick} className={HERO_CTA_CLASS}>
                            {primaryCta}
                        </button>
                    </div>
                </div>

                <div className='relative flex min-h-0 w-full flex-col items-center justify-center gap-[clamp(0.5rem,1.5vh,1rem)] md:justify-self-center'>
                    <div className='relative w-full max-w-[min(100%,clamp(15rem,42vw,22.5rem))] [@media(max-height:740px)]:max-w-[min(100%,17.5rem)]'>
                        <div className='pointer-events-none absolute left-1/2 top-1/2 h-[82%] w-[84%] -translate-x-1/2 -translate-y-1/2 rounded-[999px] bg-[radial-gradient(circle,rgba(244,80,42,0.16)_0%,rgba(244,80,42,0.08)_42%,rgba(23,19,14,0.02)_72%,rgba(23,19,14,0)_100%)] blur-[34px]' />
                        <div className='relative overflow-visible'>
                            <ChromaKeyVideo
                                src={ASSETS.heroVideoSource}
                                poster={ASSETS.heroVideoPoster}
                                settings={CHROMA_KEY_SETTINGS}
                                aria-label={t(
                                    'foodCost.hero.characterAnimationAriaLabel',
                                    'Animación del personaje Tipi',
                                )}
                                className='relative z-0 mx-auto w-full scale-[clamp(0.88,0.95,1.1)] [@media(max-height:740px)]:scale-[0.88]'
                            />
                        </div>
                    </div>

                    <div className='flex w-full flex-col items-center gap-1.5 md:hidden'>
                        <button type='button' onClick={onCtaClick} className={HERO_CTA_CLASS}>
                            {primaryCta}
                        </button>
                    </div>

                    <div className='relative z-10 flex w-full flex-col items-center gap-2 text-center'>
                        <FoodCostHeroCollaboration />
                        <p className='m-0 text-[clamp(0.75rem,1.8vw,0.9375rem)] italic text-[#B8AFA3]'>
                            {t('foodCost.hero.awardsLabel', 'Reconocida y premiada por')}
                        </p>
                        <div
                            role='group'
                            className='flex items-center justify-center gap-4'
                            aria-label={t('foodCost.hero.awardsAriaLabel', 'GOe y Basque Culinary Center')}
                        >
                            <GOELogo
                                className='h-[clamp(2rem,5vh,3.5rem)] w-auto [&>g>path]:fill-[#E8DFD2]'
                                aria-hidden
                            />
                            <BasqueLogo
                                className='h-[clamp(2rem,5vh,3.5rem)] w-auto [&>g>path]:fill-[#E8DFD2]'
                                aria-hidden
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
